// Copyright © 2021 Move Closer

import { IWindow } from '@movecloser/front-core'
import VueScroll from 'vuescroll'
import { Component, Prop, Vue, Watch } from 'vue-property-decorator'

import { Link } from '../../../dsl/front'

import { ContentSneakPeakData, Image, ImageRatio, ListConfig } from '../../_contracts'
import { prepareImage } from '../../_filters'

import { ReadMoreLink } from '../ReadMoreLink'
import { ThumbnailsListItem } from '../ThumbnailsListItem/ThumbnailsListItem'
import { MergedListItem } from '../ui/ListWithAddonsMixin'

interface ContentSneakPeakDataWithThumbnail extends ContentSneakPeakData {
  thumbnail: Image | undefined;
}

/**
 * @author Stanisław Gregor <stanislaw.gregor@movecloser.pl>
 * @author Olga Milczek <olga.milczek@movecloser.pl>
 */
@Component({
  name: 'ThumbnailsList',
  components: {
    ReadMoreLink,
    ThumbnailsListItem,
    VueScroll
  },

  template: `
    <div class="thumbnails-list">
      <div class="thumbnails-list__container" :class="{ '--with-read-more' : readMoreLink }">
        <!-- Articles list -->
        <ul v-if="articleItems && articleItems.length" class="list-unstyled">
          <template v-for="item in articleItems">
            <ThumbnailsListItem
              v-if="item.type === 'article'"
              :key="item.key"
              :item="item"
              :list-config="listConfig" />
          </template>
        </ul>
        <!-- END | Articles list -->
      </div>

      <!-- "Read more" button -->
      <div v-if="readMoreLink" class="thumbnails-list__read-more-link">
        <ReadMoreLink :link="readMoreLink" />
      </div>
    </div>
  `
})
export class ThumbnailsList extends Vue {
  /**
   * Articles to be rendered.
   */
  @Prop({
    type: Array,
    required: true
  })
  public readonly articles!: ContentSneakPeakDataWithThumbnail[]

  /**
   * Fallback cover image to display.
   */
  @Prop({
    type: String,
    required: false,
    default: ''
  })
  protected readonly fallbackImage!: string

  @Prop({
    type: Boolean,
    required: false,
    default: true
  })
  protected readonly isLazy!: boolean

  /**
   * "Read more" link.
   */
  @Prop({
    type: Object,
    required: false
  })
  public readonly readMoreLink?: Link

  @Prop({
    type: Object,
    required: true
  })
  protected readonly windowService!: IWindow

  /**
   * ArticlesList Config.
   */
  @Prop({
    type: Object,
    required: false,
    default: () => {
      return { showArticleTags: false }
    }
  })
  public readonly listConfig?: ListConfig

  @Watch('articles', { immediate: true })
  resolveArticleImages (): void {
    this.articles.map((article: ContentSneakPeakDataWithThumbnail) => {
      article.thumbnail = this.getThumbnail(article)
    })
  }

  /**
   * Converts articles to MergedListItem format for ThumbnailsListItem
   */
  public get articleItems (): MergedListItem[] {
    return this.articles.map((article, index) => ({
      type: 'article' as const,
      component: '',
      data: article,
      props: {},
      position: index,
      key: `article-${article.id || index}`
    }))
  }

  /**
   * Resolves the applicable `Image` for the current screen resolution.
   */
  public getThumbnail (article: ContentSneakPeakData): Image | undefined {
    if (
      typeof article.properties.cover !== 'object' || typeof article.properties.cover === null ||
      typeof article.properties.cover.image !== 'object' || article.properties.cover.image === null
    ) {
      return {
        src: '',
        width: null,
        height: null
      }
    }

    return prepareImage(article.properties.cover.image, ImageRatio.Standard169, 90, this.isLazy)
  }
}
